import {
  Box,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  Tooltip,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';
import { Editor } from '@tiptap/core';
import { ReactComponent as HeaderColumnIcon } from 'assets/icons/header_column.svg';
import { ReactComponent as HeaderRowIcon } from 'assets/icons/header_row.svg';
import { ReactComponent as HeaderIcon } from 'assets/icons/headers.svg';
import { Mixpanel, TABLE_HEADER_EDITED } from 'libs/third-party/Mixpanel';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { preventDefault, stopPropagation } from 'utils/event';

import { getTableData } from '../TableWidget.helpers';

type HeaderOptionProps = {
  editor: Editor;
  onChange: () => void;
};

const getHeaderInfo = (editor: Editor) => {
  const { cells } = getTableData(editor.state.doc, true);

  let isHeaderRow = false,
    isHeaderColumn = false;

  if (cells.length > 0) {
    // Check header row - all cells in first row should be headers
    const firstRowCells = cells.filter((cell) => cell.rowIndex === 0);
    isHeaderRow = firstRowCells.every((cell) => editor.state.doc.nodeAt(cell.position)?.type.name === 'tableHeader');

    // Check header column - all cells starting at column 0 should be headers
    const firstColumnCells = cells.filter((cell) => cell.colIndex === 0);
    isHeaderColumn = firstColumnCells.every(
      (cell) => editor.state.doc.nodeAt(cell.position)?.type.name === 'tableHeader',
    );
  }

  return {
    isHeaderRow,
    isHeaderColumn,
  };
};

export const HeaderOption = memo(({ editor, onChange }: HeaderOptionProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'tableToolbar.header' });
  const label = t('label');
  const ariaLabel = t('ariaLabel');
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [isHover, setIsHover] = useBoolean();
  const [shouldReturnFocus, setShouldReturnFocus] = useBoolean(true);

  const onTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setShouldReturnFocus.on();
    onToggle();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isOpen) return stopPropagation(e);
  };

  return (
    <Tooltip isOpen={!isOpen && isHover} hasArrow placement='bottom' label={label} bg='black'>
      <Box onKeyDown={onKeyDown}>
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          modifiers={[{ name: 'eventListeners', options: { scroll: false } }]}
          returnFocusOnClose={shouldReturnFocus}
          placement='bottom-start'
          isLazy
        >
          <PopoverTrigger>
            <IconButton
              onMouseEnter={setIsHover.on}
              onMouseLeave={setIsHover.off}
              onClick={onTriggerClick}
              isActive={false}
              onMouseDown={preventDefault}
              size='sm'
              aria-label={ariaLabel}
              icon={<HeaderIcon />}
              bg={'white'}
            />
          </PopoverTrigger>
          <Box zIndex='fontMenu'>
            <PopoverContent w='200px'>
              <HeaderOptionContent editor={editor} onChange={onChange} />
            </PopoverContent>
          </Box>
        </Popover>
      </Box>
    </Tooltip>
  );
});
HeaderOption.displayName = 'HeaderOption';

const HeaderOptionContent = memo(({ editor, onChange }: HeaderOptionProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'tableToolbar.header' });
  const { isHeaderRow, isHeaderColumn } = getHeaderInfo(editor);

  const toggleHeaderRow = () => {
    editor.chain().focus().toggleHeaderRow().run();
    onChange();

    Mixpanel.track(TABLE_HEADER_EDITED, { header_type: 'Row', number_of_headers: isHeaderRow ? 0 : 1 });
  };

  const toggleHeaderColumn = () => {
    editor.chain().focus().toggleHeaderColumn().run();
    onChange();

    Mixpanel.track(TABLE_HEADER_EDITED, { header_type: 'Column', number_of_headers: isHeaderColumn ? 0 : 1 });
  };

  return (
    <Box p={'16px'} gap={'8px'} display={'grid'} gridRowGap={'8px'}>
      <Box display={'grid'} gridTemplateColumns={'auto 34px'}>
        <Box p={'4px'} display={'grid'} gridTemplateColumns={'20px auto'}>
          <HeaderRowIcon style={{ alignSelf: 'center' }} />
          <Text fontSize={'xs'} pl={'10px'}>
            {t('row.label')}
          </Text>
        </Box>
        <Switch
          aria-label={t('row.ariaLabel')}
          alignSelf={'center'}
          size='md'
          isChecked={isHeaderRow}
          onChange={toggleHeaderRow}
        />
      </Box>
      <Box display={'grid'} gridTemplateColumns={'auto 34px'}>
        <Box p={'4px'} display={'grid'} gridTemplateColumns={'20px auto'}>
          <HeaderColumnIcon style={{ alignSelf: 'center' }} />
          <Text fontSize={'xs'} pl={'10px'}>
            {t('column.label')}
          </Text>
        </Box>
        <Switch
          aria-label={t('column.ariaLabel')}
          alignSelf={'center'}
          size='md'
          isChecked={isHeaderColumn}
          onChange={toggleHeaderColumn}
        />
      </Box>
    </Box>
  );
});
HeaderOptionContent.displayName = 'HeaderOptionContent';
